// ── Two step sign in (Firebase multi-factor auth) ────────────────────────────
// Authenticator app codes (TOTP: Google Authenticator, Microsoft
// Authenticator, Authy…). Free: nothing is sent, the code is made on the
// user's phone. Everything runs on the master project's auth (masterAuth)
// only; tenant projects never sign anyone in.
//
// Needs, on the master project: Identity Platform, and the TOTP MFA provider
// switched on (an API setting, not in the console screens; see CLAUDE.md).
//
// Sign in:  signInWithEmailAndPassword throws auth/multi-factor-auth-required
//           → startSignInMfa(err) → finishSignInMfa(code).
// Turn on:  startTotpEnroll() → QR code / key → finishTotpEnroll(code).
//           Needs a verified email.
// Turn off: removeFactor(uid).
//
// SMS was dropped entirely (it costs per message and nobody used it).

import {
  TotpMultiFactorGenerator,
  getMultiFactorResolver, multiFactor, sendEmailVerification,
  type MultiFactorError, type MultiFactorResolver, type MultiFactorInfo, type TotpSecret,
} from 'firebase/auth';
import { masterAuth } from '../firebase/master';

/** Name shown in the authenticator app next to the user's email. */
export const TOTP_ISSUER = 'DiaWolt';

export function isMfaRequired(err: unknown): boolean {
  return (err as { code?: string })?.code === 'auth/multi-factor-auth-required';
}

// ── Sign in ─────────────────────────────────────────────────────────────────

export interface MfaChallenge {
  resolver:  MultiFactorResolver;
  factorUid: string;
}

/** Password was right but the authenticator code is needed too. */
export function startSignInMfa(err: unknown): MfaChallenge {
  const resolver = getMultiFactorResolver(masterAuth, err as MultiFactorError);
  const totp = resolver.hints.find(h => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
  if (!totp) throw Object.assign(new Error('No authenticator app is set up for this account.'), { code: 'auth/no-second-factor' });
  return { resolver, factorUid: totp.uid };
}

/** Finishes the sign-in; AuthContext's onAuthStateChanged takes over. */
export async function finishSignInMfa(ch: MfaChallenge, code: string): Promise<void> {
  await ch.resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(ch.factorUid, code.trim()));
}

// ── Turning it on / off for the signed-in user ──────────────────────────────

/** The authenticator app(s) on the signed-in account. */
export function enrolledFactors(): MultiFactorInfo[] {
  const u = masterAuth.currentUser;
  return u ? multiFactor(u).enrolledFactors.filter(f => f.factorId === TotpMultiFactorGenerator.FACTOR_ID) : [];
}

/** Latest emailVerified flag (the cached one goes stale after the user
 *  clicks the link in another tab). */
export async function refreshEmailVerified(): Promise<boolean> {
  const u = masterAuth.currentUser;
  if (!u) return false;
  await u.reload();
  return u.emailVerified;
}

export async function sendVerifyEmail(): Promise<void> {
  const u = masterAuth.currentUser;
  if (u) await sendEmailVerification(u);
}

export interface TotpSetup {
  secret:    TotpSecret;
  /** otpauth:// link: drawn as a QR code for the app to scan. */
  qrUrl:     string;
  /** The same secret as text, for typing into the app by hand. */
  secretKey: string;
}

/** Step 1 of turning it on: a new secret for the authenticator app. */
export async function startTotpEnroll(): Promise<TotpSetup> {
  const u = masterAuth.currentUser;
  if (!u) throw new Error('Not signed in.');
  const session = await multiFactor(u).getSession();
  const secret  = await TotpMultiFactorGenerator.generateSecret(session);
  return { secret, qrUrl: secret.generateQrCodeUrl(u.email ?? u.uid, TOTP_ISSUER), secretKey: secret.secretKey };
}

/** Step 2: the first code from the app proves it's set up right. */
export async function finishTotpEnroll(secret: TotpSecret, code: string): Promise<void> {
  const u = masterAuth.currentUser;
  if (!u) throw new Error('Not signed in.');
  await multiFactor(u).enroll(TotpMultiFactorGenerator.assertionForEnrollment(secret, code.trim()), 'Authenticator app');
}

export async function removeFactor(factorUid: string): Promise<void> {
  const u = masterAuth.currentUser;
  if (!u) throw new Error('Not signed in.');
  await multiFactor(u).unenroll(factorUid);
}

/** Plain English for the Firebase errors these flows can hit. */
export function mfaErrorText(err: unknown): string {
  const code = (err as { code?: string })?.code ?? '';
  switch (code) {
    case 'auth/invalid-verification-code':
    case 'auth/totp-challenge-timeout':     return 'That code is not right. Check the app and try again.';
    case 'auth/code-expired':               return 'That code has expired. Use the newest one from the app.';
    case 'auth/missing-code':               return 'Enter the 6 digit code.';
    case 'auth/too-many-requests':          return 'Too many tries. Wait a few minutes and try again.';
    case 'auth/unverified-email':           return 'Verify your email address first.';
    case 'auth/requires-recent-login':      return 'For safety, sign out and sign in again, then try once more.';
    case 'auth/maximum-second-factor-count-exceeded': return 'This account already has the most second steps allowed.';
    case 'auth/unsupported-first-factor':
    case 'auth/operation-not-allowed':      return 'Authenticator app sign in is not switched on for this app yet. Contact DiaWolt support.';
    case 'auth/no-second-factor':           return 'No second step is set up for this account. Contact DiaWolt support.';
    case 'auth/network-request-failed':     return 'No connection. Check your internet and try again.';
    default:                                return err instanceof Error && err.message ? err.message : 'Something went wrong. Please try again.';
  }
}
